/**
 * Block Renderer Utilities
 * 
 * Shared helper functions and utilities for block rendering across
 * UIBlockRenderer and ProgressiveBlockRenderer to eliminate duplication.
 */

import React from 'react';
import { Container } from '@strivelabs/ui-blocks';
import type { UIBlock, ContainerBlock, UIActionEvent, BlockBehaviour, DataTableClientBlock } from '@/types/ui-blocks';
import {
  TextBlockRenderer,
  ConfirmationBlockRenderer,
  SelectBlockRenderer,
  RadioBlockRenderer,
  EditableListBlockRenderer,
  ChartBlockRenderer,
  TabbedChartBlockRenderer,
  TableBlockRenderer,
  DataTableClientBlockRenderer,
  InlineSuggestionsBlockRenderer,
  MetricsGridBlockRenderer,
  StackedSuggestionsBlockRenderer,
  ContentListBlockRenderer,
} from './';
import type { TabbedChartWireContent } from './chart-wire-types';
import BlockErrorBoundary from './BlockErrorBoundary';

/**
 * Returns auto-triggered behaviours from a block, if any.
 */
export const getAutoBehaviours = (block: UIBlock | ContainerBlock): BlockBehaviour[] => {
  const behaviours = (block as UIBlock & { behaviours?: BlockBehaviour[] }).behaviours;
  if (!behaviours || !Array.isArray(behaviours)) return [];
  return behaviours.filter((b) => b.trigger === 'auto');
};

/**
 * Type guard to check if a block is a container
 */
export const isContainer = (block: UIBlock | ContainerBlock): block is ContainerBlock => {
  return 'type' in block && block.type === 'container';
};

/**
 * Renders error UI for unknown block types
 */
export const renderUnknownBlockError = (block: UIBlock, index: number): React.ReactNode => {
  return (
    <div key={index} className="p-4 border rounded-lg bg-destructive/10">
      <p className="text-sm text-destructive">
        Unknown block type: {block.type}
      </p>
      <pre className="mt-2 text-xs text-muted-foreground">
        {JSON.stringify(block, null, 2)}
      </pre>
    </div>
  );
};

/**
 * Renders a container block with inner blocks
 * 
 * @param containerBlock - The container block to render
 * @param innerBlocksRenderer - Function to render inner blocks
 * @param options - Options for container rendering
 */
export const renderContainerBlock = (
  containerBlock: ContainerBlock,
  innerBlocksRenderer: (blocks: (UIBlock | ContainerBlock)[]) => React.ReactNode[],
  options?: {
    /** Include wrapper div for semantic keying (default: false) */
    includeWrapper?: boolean;
    /** Index for key generation when wrapper is included */
    index?: number;
  }
): React.ReactNode => {
  const {
    includeWrapper = false,
    index = 0,
  } = options || {};

  const innerBlocks = containerBlock.blocks || [];

  const containerElement = (
    <BlockErrorBoundary>
      <Container
        layout={containerBlock.layout}
        gap={containerBlock.gap}
        columns={containerBlock.columns}
        padding={containerBlock.padding}
        maxWidth={containerBlock.maxWidth}
        height={containerBlock.height}
        scroll={containerBlock.scroll}
        id={containerBlock.id}
        label={containerBlock.label}
        border={containerBlock.border}
        borderColor={containerBlock.borderColor}
        width={containerBlock.width}
        heading={containerBlock.heading}
        headingBadge={containerBlock.headingBadge}
        description={containerBlock.description}
      >
        {innerBlocksRenderer(innerBlocks)}
      </Container>
    </BlockErrorBoundary>
  );

  if (includeWrapper) {
    return (
      <div key={`container-${containerBlock.id || index}`}>
        {containerElement}
      </div>
    );
  }

  return containerElement;
};

/**
 * Options for block dispatcher
 */
export interface BlockDispatcherOptions {
  /** The block to render */
  block: UIBlock | ContainerBlock;
  /** Callback fired when user interacts with UI components */
  onUIAction: (event: UIActionEvent) => void;
  /** Optional callback for suggestion clicks (for progressive renderer) */
  onSuggestionClick?: (text: string, prefillOnly?: boolean) => void;
  /** When true, interactive elements are disabled */
  readOnly?: boolean;
  /** Optional renderer functions for context-specific overrides */
  customRenderers?: {
    /** Custom stacked_suggestions renderer (e.g., for chat-specific UI) */
    stackedSuggestions?: (block: UIBlock, onSuggestionClick?: (text: string, prefillOnly?: boolean) => void) => React.ReactNode;
  };
}

/**
 * Unified block dispatcher
 * 
 * Centralizes block type → component mapping to eliminate duplication
 * between UIBlockRenderer and ProgressiveBlockRenderer. Supports
 * context-specific overrides while maintaining consistent behavior.
 */
/**
 * Block types that require a non-null content object to render.
 * If the LLM returns content: null for any of these, the block is
 * silently skipped rather than crashing the renderer.
 */
const CONTENT_REQUIRED_BLOCK_TYPES = new Set([
  'confirmation', 'select', 'radio', 'editable_list',
  'line_chart', 'bar_chart', 'area_chart', 'pie_chart', 'radial_chart', 'scatter_chart',
  'table', 'data_table_client', 'tabbed_chart', 'metrics_grid', 'stacked_suggestions',
  'inline_suggestions', 'content_list',
]);

export const dispatchBlock = (options: BlockDispatcherOptions): React.ReactNode => {
  const { block, onUIAction, onSuggestionClick, readOnly, customRenderers } = options;

  // Guard: skip blocks with missing content when the block type requires it.
  // This handles malformed LLM output (content: null) that would crash individual renderers.
  if (CONTENT_REQUIRED_BLOCK_TYPES.has(block.type) && 'content' in block && !block.content) {
    return null;
  }

  let rendered: React.ReactNode;

  switch (block.type) {
    case 'text':
      rendered = <TextBlockRenderer block={block} />;
      break;

    case 'confirmation':
      rendered = (
        <ConfirmationBlockRenderer
          block={block}
          onUIAction={onUIAction}
          readOnly={readOnly}
        />
      );
      break;

    case 'select':
      rendered = (
        <SelectBlockRenderer
          block={block}
          onUIAction={onUIAction}
          readOnly={readOnly}
        />
      );
      break;

    case 'radio':
      rendered = (
        <RadioBlockRenderer
          block={block}
          onUIAction={onUIAction}
          readOnly={readOnly}
        />
      );
      break;

    case 'editable_list':
      rendered = (
        <EditableListBlockRenderer
          block={block}
          onUIAction={onUIAction}
          readOnly={readOnly}
        />
      );
      break;

    case 'line_chart':
    case 'bar_chart':
    case 'area_chart':
    case 'pie_chart':
    case 'radial_chart':
    case 'scatter_chart':
      rendered = (
        <ChartBlockRenderer
          block={block}
        />
      );
      break;

    case 'table':
      rendered = (
        <TableBlockRenderer
          block={block}
        />
      );
      break;

    case 'data_table_client':
      rendered = (
        <DataTableClientBlockRenderer
          block={block as DataTableClientBlock}
        />
      );
      break;

    case 'stacked_suggestions':
      // Use custom renderer if provided (for chat context)
      if (customRenderers?.stackedSuggestions) {
        rendered = customRenderers.stackedSuggestions(block as UIBlock, onSuggestionClick);
      } else {
        // Default: use standard stacked suggestions renderer
        rendered = (
          <StackedSuggestionsBlockRenderer
            block={block as UIBlock}
            onUIAction={onUIAction}
            readOnly={readOnly}
          />
        );
      }
      break;

    case 'inline_suggestions':
      rendered = (
        <InlineSuggestionsBlockRenderer
          block={block as UIBlock}
          onUIAction={onUIAction}
          readOnly={readOnly}
        />
      );
      break;

    case 'metrics_grid':
      rendered = (
        <MetricsGridBlockRenderer
          block={block as UIBlock}
          onUIAction={onUIAction}
        />
      );
      break;

    case 'content_list':
      rendered = (
        <ContentListBlockRenderer
          block={block as UIBlock}
        />
      );
      break;

    case 'tabbed_chart':
      rendered = (
        <TabbedChartBlockRenderer
          block={block as unknown as { content: TabbedChartWireContent }}
        />
      );
      break;

    default:
      rendered = renderUnknownBlockError(block as UIBlock, 0);
      break;
  }

  return <BlockErrorBoundary>{rendered}</BlockErrorBoundary>;
};
